/*
* Copyright  2025-2026, Airspan
* All Rights Reserved
*
* This code is unpublished, confidential and proprietary to Airspan and may only be used in accordance with the appropriate
* license agreement. All other copying, modification, distribution, or
* reverse engineering are strictly prohibited.
*/

import React, { useCallback, useEffect, useState, useImperativeHandle } from "react";
import { SxProps } from "@mui/material";
import { Button } from "../../ui/button";
import { Loader2 } from "lucide-react";
import CustomDataGrid from "../Table/CustomDataGrid";
import { GridColDef, GridRowId, GridSortModel } from "@mui/x-data-grid";
import { IFilter } from "../Filter/types";
import AssociationFilter from "./AssociationFilter";
import ActiveFilterChips from "../Filter/ActiveFilterChips";
import AddButton from "../Buttons/AddButton";
import { Icon } from "../Icon";
import { paginationModelSelector } from "../../../store/helpers/helperSelectors";
import { useSelector } from "react-redux";
import { initialState } from "../../../store/helpers/helperInitialState";
import { searchKeywordSelector } from "../../../store/search/selector";
import { resetKeyword } from "../../../store/search/reducer";
import { setBasicDialogOpenFor, setOpenToast } from "../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../store/hooks";
import { errorMessage } from "../../../utils/helpers";
import { useAssociationMutations, createPayload, useLoadData, useUrlParameter } from "./AssociationHelper";


interface IAssociationProps {
  entity?: any;
  filters?: IFilter;
  parentEntityName?: string;
  childEntityName?: string;
  columns?: any[];
  addSorting?: string;
  resetFiltersHandler?: (state: any) => void,
  showAddAction?: boolean;
  showAddAll?: boolean;
  checkboxSelection?: boolean;
  disableColumnMenu?: boolean;
  disableRowSelectionOnClick?: boolean;
  addMutationKey?: any;
  addMutationPayloadKey?: any[];
  urlAddParameterKey?: string;
  loadAddDataKey?: string;
  loadAddAllDataKey?: string;
  showInfoMessage?: boolean;
  infoMessage?: string;
  rowSelectable?: (param: any) => boolean;
  setRowClass?: (param: any) => string;
  disabledActionButton?: (param: any) => boolean;
  sx?: SxProps;
  setIsAssociatedAdded?: (status: boolean) => void; urlListParameterKey?: string;
  onRowClick?: (params: any, event: any, details: any) => void;
  selectedRowIds: GridRowId[];
  setSelectedRowIds: React.Dispatch<React.SetStateAction<GridRowId[]>>;
  addAssociationLoader: boolean;
  setAddAssociationLoader: React.Dispatch<React.SetStateAction<boolean>>;
  paginationTableId?: string;
}

const Association = React.forwardRef(function Association(props: IAssociationProps, ref: React.Ref<any>) {
    const {
    entity,
    filters,
    parentEntityName,
    childEntityName,
    columns = [],
    addSorting,
    resetFiltersHandler,
    showAddAction = true,
    showAddAll = true,
    checkboxSelection = true,
    disableColumnMenu = true,
    disableRowSelectionOnClick = true,
    addMutationKey,
    addMutationPayloadKey = [],
    urlAddParameterKey,
    loadAddDataKey,
    loadAddAllDataKey,
    showInfoMessage = false,
    infoMessage = "",
    rowSelectable,
    setRowClass,
    disabledActionButton,
    sx,
    setIsAssociatedAdded,
    onRowClick,
    selectedRowIds,
    setSelectedRowIds,
    addAssociationLoader,
    setAddAssociationLoader,
    paginationTableId,
  } = props;
  const dispatch = useStoreDispatch();
  const { executeMutation } = useAssociationMutations();

  const paginationModel = useSelector(paginationModelSelector);
  const keyword = useSelector(searchKeywordSelector);
  const unMappedTableId = paginationTableId || "UnMappedData";
  const UnmappedAddData = paginationModel?.[unMappedTableId] || initialState.pagination;
  const [sortOrderAdd, setSortOrderAdd] = useState(addSorting || "");

  const addFilters = useUrlParameter(urlAddParameterKey ?? "");
  addFilters.set("sort", sortOrderAdd);
  addFilters.set("page", UnmappedAddData.page.toString());
  addFilters.set("size", UnmappedAddData.pageSize.toString());
  addFilters.set("search", keyword);
  const argsFilter = { filters: addFilters.toString(), id: entity?.value };

  const { data: unmappedData, isLoading: isLoadingUnmap, refetch: refetchUnmap } = useLoadData(loadAddDataKey ?? "", argsFilter);
  const { data: allUnmappedData, refetch: refetchAllUnmap } = useLoadData(loadAddAllDataKey ?? "", argsFilter);

  let unmappedRowsData = (unmappedData as any)?.data ?? (unmappedData as any)?.content ?? [];
  unmappedRowsData = unmappedRowsData.map((row: any) => {
      if (row?.siteGroupId === "999999999" || row?.siteGroupId === "999999998") {
        return {
          ...row,
          siteGroupName: "",
        };
      }
      return row;
    });
   const unmappedRowsCount = (unmappedData as any)?.totalItems || (unmappedData as any)?.totalElements || 0;

  
  const handleClearSearch = useCallback(() => {
    dispatch(resetKeyword());
  }, [dispatch]);

  const handleClearAllFilters = useCallback(() => {
    if (resetFiltersHandler) {
      dispatch(resetFiltersHandler({}) as any);
    } else {
      filters?.selectFilters?.forEach((filter) => {
        if (filter.id === "geoRegion" || filter.id === "addDeviceGeoRegion") return;
        dispatch(filter.setValue(filter.multiple ? [] : ""));
      });
    }
    dispatch(resetKeyword());
  }, [dispatch, filters?.selectFilters, resetFiltersHandler]);

  const childKey = (allUnmappedData as any[] | undefined ?? []).map((unmapped) => unmapped.objectKey ?? unmapped.id);

  useEffect(() => {
    refetchUnmap();
    refetchAllUnmap();
  }, [UnmappedAddData.page, UnmappedAddData.pageSize, argsFilter.filters, refetchUnmap, refetchAllUnmap]);

  const addRecords = (ids = selectedRowIds, isAddAll = false) => {
    if (isAddAll) {
      addAssociationHandler?.(ids, isAddAll);

    } else if (ids.length > 0) {
      addAssociationHandler?.(ids, isAddAll);
      setSelectedRowIds([]);
    }
  };

  useImperativeHandle(ref, () => ({
    addRecords
  }));

  const addAssociationHandler = async (selectedRowIds: GridRowId[], isAddAll: boolean = false) => {
    if (!isAddAll && selectedRowIds.length <= 0)
      return;

    if (childKey.length === 0 && isAddAll === true) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: `At least one ${childEntityName?.toLowerCase()} is required.` }));
      return;
    }
    try {
      setAddAssociationLoader(true);
      const response = await executeMutation(addMutationKey,
        createPayload(
          addMutationPayloadKey,
          [
            entity?.value,
            isAddAll
              ? childKey
              : selectedRowIds.map(id => id.toString()),
          ]
        )
      );
      if ('data' in response && response.data?.status === 200) {
        setAddAssociationLoader(false);
        setSelectedRowIds([]);

        setIsAssociatedAdded?.(true);
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: `${childEntityName}${setSelectedRowIds.length > 1 ? 's have' : ' has'} successfully added to ${parentEntityName}: ${entity?.text}.`,
          })
        );
        dispatch(setBasicDialogOpenFor(undefined));
      }
      else if ('error' in response && response?.error)
        throw response?.error;
    }
    catch (error) {
      setAddAssociationLoader(false);
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const actionColumn = (): GridColDef => ({
    field: "actions",
    type: "actions",
    width: 100,
    cellClassName: "actions",
    getActions: (row) => {
      return [
        <AddButton
          key={`add-${row.id}`}
          id={row.id} isLoading={addAssociationLoader} disabled={disabledActionButton?.(row) ?? false} addHandler={(ids) => {
            setSelectedRowIds([ids]);
            addRecords([ids]);
          }} />,
      ];
    },
  });

  const getSortBy = (sortModel: GridSortModel) => {
    if (sortModel.length > 0) {
      return Object.values(sortModel[0]).toString() as string;
    } else return "";
  };

  const basecolumns = React.useMemo<GridColDef[]>(() => {
    if (showAddAction)
      return [...columns, actionColumn()];
    else
      return columns;
  }, []);

  return (
    <div className="p-0">
      <div className="mb-5 m-0 flex items-center">
        <AssociationFilter
          searchTooltip={`Search by '${childEntityName}' fields for quick access.`}
          selectFilters={filters?.selectFilters}
          dateFilters={filters?.dateFilters}
          resetFilters={resetFiltersHandler}
          searchPlaceholder={`Search ${childEntityName?.toLowerCase()}s...`}
        />
      </div>

      {showInfoMessage && (
        <div className="flex items-center gap-2 text-sm italic text-link">
          <Icon name="info" size={16} className="shrink-0" />
          <span>{infoMessage}</span>
        </div>
      )}
       <div className="flex justify-between items-end mb-0 mt-4">
       <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">{`${unmappedRowsData?.length || 0} results`}</span>
        <ActiveFilterChips
          filters={filters?.selectFilters || []}
          searchKeyword={keyword}
          onClearSearch={handleClearSearch}
          onClearAll={handleClearAllFilters}
          dispatch={dispatch}
        />
      </div>
       </div>
      <div className="w-full pt-1">
        <CustomDataGrid
          tableId={unMappedTableId}
          rows={unmappedRowsData ?? []}
          rowCount={unmappedRowsCount}
          loading={isLoadingUnmap}
          columns={basecolumns}
          checkboxSelection={checkboxSelection}
          disableColumnMenu={disableColumnMenu}
          disableRowSelectionOnClick={disableRowSelectionOnClick}
          onRowClick={onRowClick}
          onRowSelectionModelChange={(rowIds) => setSelectedRowIds(rowIds)}
          rowSelectionModel={selectedRowIds}
          getRowId={(row: any) => row.objectKey ?? row.id}
          onSortModelChange={(params: GridSortModel) => setSortOrderAdd(getSortBy(params))}
          isRowSelectable={(params) => rowSelectable?.(params) ?? true}
          getRowClassName={(params) => setRowClass?.(params) ?? ""}
          sx={{
            backgroundColor: 'var(--background)',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: 'var(--background)', borderBottom: '1px solid var(--border)' },
            '& .MuiDataGrid-virtualScroller': { backgroundColor: 'var(--background)' },
            '& .MuiDataGrid-footerContainer': { backgroundColor: 'var(--background)', justifyContent: 'flex-end', borderTop: '1px solid var(--border)' },
            '& .MuiDataGrid-selectedRowCount': { display: 'none' },
            '& .MuiDataGrid-scrollbar--horizontal': { display: 'none' },
            '& .MuiDataGrid-filler': { backgroundColor: 'var(--background)' },
            '& .MuiDataGrid-scrollbarFiller': { backgroundColor: 'var(--background)' },
            '& .MuiDataGrid-overlay': { backgroundColor: 'var(--background)' },
            '& .MuiDataGrid-overlayWrapper': { backgroundColor: 'var(--background)' },
            ...((sx as any) || {}),
          }}
          fillContainer
        />
      </div>
    </div>
  );
});
export default Association;